import type { ComponentType } from "react";
import { PlatformSelectStep } from "./steps/platform-select-step";
import { InstallNodeStep } from "./steps/install-node-step";
import { InstallGHStep } from "./steps/install-gh-step";
import { AccessPermissionsStep } from "./steps/access-permissions-step";
import { getCLISetupSteps } from "./steps/cli-setup-steps";
import { VerificationStep } from "./steps/verification-step";
import { ConfigureMcpStep } from "./steps/configure-mcp-step";
import {
  WhatIsRequestStep,
  CopyRequestCommandStep,
  FollowPromptsStep,
} from "./steps/create-request-steps";
import {
  WhatIsPlannerStep,
  RunPlannerStep,
  ReviewPlanStep,
} from "./steps/plan-epic-steps";
import {
  WhatIsOrchestratorStep,
  RunOrchestratorStep,
  MonitorProgressStep,
} from "./steps/execute-steps";
import {
  ReviewOverviewStep,
  ValidateOutputStep,
  IterateStep,
} from "./steps/review-steps";
import {
  WhatIsDesignerStep,
  DesignerPlatformPickerStep,
  DesignerPrerequisitesStep,
  UseDesignerStep,
} from "./steps/designer-steps";
import {
  WhatIsPullRequestStep,
  RunPullRequestStep,
  ReviewCycleStep,
} from "./steps/pullrequest-steps";
import {
  WhatIsUpdateStep,
  CheckUpdatesStep,
  UpdateTroubleshootingStep,
} from "./steps/update-steps";
import {
  WhatIsBugBridgeStep,
  RunBugBridgeStep,
} from "./steps/bug-bridge-steps";
import {
  WhatIsFogBugzFormulatorStep,
  FileFogBugzCaseStep,
} from "./steps/fogbugz-formulator-steps";
import {
  WhatIsInspectorStep,
  RunInspectorStep,
} from "./steps/fogbugz-inspector-steps";
import { FogBugzSetupStep } from "./steps/fogbugz-setup-step";

export type HelpFlowTone = "violet" | "blue" | "emerald" | "amber" | "rose";

export interface HelpStepComponentProps {
  flow: HelpFlow;
  step: HelpStep;
  stepIndex: number;
  totalSteps: number;
  onAdvance?: () => void;
  onNavigateToFlow?: (flowId: string) => void;
}

export interface HelpStep {
  id: string;
  title: string;
  description: string;
  component: ComponentType<HelpStepComponentProps>;
  prerequisites?: string[];
}

export interface HelpFlow {
  id: string;
  title: string;
  icon: string;
  description: string;
  stepCount: number;
  estimatedTime: string;
  tone: HelpFlowTone;
  badge: string;
  steps: HelpStep[];
}


const CLI_SETUP_STATIC_STEPS = getCLISetupSteps("mac");

const CLI_SETUP_FLOW_STEPS: HelpStep[] = [
  {
    id: "platform-select",
    title: "Select your platform",
    description:
      "Choose the machine and shell you work in so every command, path, and verification step lines up with your environment.",
    component: PlatformSelectStep,
  },
  {
    id: "access-permissions",
    title: "Check access permissions",
    description:
      "Your CLI host account and GitHub Enterprise access are mandatory. Without them, nothing else in this guide will work.",
    component: AccessPermissionsStep,
  },
  {
    id: "install-node",
    title: "Install Node.js",
    description:
      "Install Node.js and npm — the runtime and package manager SpecTree needs to function.",
    component: InstallNodeStep,
  },
  {
    id: "install-gh",
    title: "Install GitHub CLI",
    description:
      "Install the GitHub CLI tool that handles authentication and package access.",
    component: InstallGHStep,
  },
  ...CLI_SETUP_STATIC_STEPS,
  {
    id: "configure-mcp",
    title: "Connect your CLI host to SpecTree",
    description:
      "Set up MCP so your CLI host can communicate with SpecTree.",
    component: ConfigureMcpStep,
  },
  {
    id: "launch-copilot",
    title: "Launch your CLI host",
    description:
      "Start your CLI host in your terminal and take your first step with SpecTree.",
    component: VerificationStep,
  },
];

export const HELP_FLOWS: HelpFlow[] = [
  {
    id: "cli-setup",
    title: "Set Up Your Environment",
    icon: "Terminal",
    description:
      "Get GitHub CLI, npm, and SpecTree working together so you can run every onboarding command with confidence.",
    stepCount: CLI_SETUP_FLOW_STEPS.length,
    estimatedTime: "10-15 min",
    tone: "violet",
    badge: "Best first step",
    steps: CLI_SETUP_FLOW_STEPS,
  },
  {
    id: "create-request",
    title: "Create an Epic Request",
    icon: "Lightbulb",
    description:
      "Learn how to turn a rough idea into a structured proposal your team can review and approve.",
    stepCount: 3,
    estimatedTime: "5 min",
    tone: "blue",
    badge: "Idea to proposal",
    steps: [
      {
        id: "what-is-request",
        title: "Tell us what you want to build",
        description:
          "You describe what you want to build or fix. SpecTree handles the solution, the plan, and everything else.",
        component: WhatIsRequestStep,
      },
      {
        id: "copy-command",
        title: "Launch the Request Formulator",
        description:
          "Use your CLI host to start the guided request interview instead of filling out a blank form alone.",
        component: CopyRequestCommandStep,
      },
      {
        id: "follow-prompts",
        title: "Complete the interview & approve",
        description:
          "Walk through the interview, submit your request, approve it on spectree.toro.com, and get ready for planning.",
        component: FollowPromptsStep,
      },
    ],
  },
  {
    id: "plan-epic",
    title: "Plan an Epic",
    icon: "Folder",
    description:
      "See how Planner transforms an approved request into an executable epic with features, tasks, and acceptance criteria.",
    stepCount: 3,
    estimatedTime: "5 min",
    tone: "emerald",
    badge: "From request to plan",
    steps: [
      {
        id: "what-is-planner",
        title: "See what Planner creates",
        description:
          "Planner gives your team an implementation-ready structure instead of leaving execution details open-ended.",
        component: WhatIsPlannerStep,
      },
      {
        id: "run-planner",
        title: "Run Planner from your CLI host",
        description:
          "Kick off planning with a terminal command, then let SpecTree generate the first full draft of your epic.",
        component: RunPlannerStep,
      },
      {
        id: "review-plan",
        title: "Review the generated plan",
        description:
          "Take a moment to confirm the scope, ordering, and task detail before any execution work begins.",
        component: ReviewPlanStep,
      },
    ],
  },
  {
    id: "execute",
    title: "Execute with Orchestrator",
    icon: "Play",
    description:
      "Understand how Orchestrator works through the plan, writes code, and keeps progress visible inside SpecTree.",
    stepCount: 3,
    estimatedTime: "3 min",
    tone: "amber",
    badge: "Agent execution",
    steps: [
      {
        id: "what-is-orchestrator",
        title: "Meet the execution engine",
        description:
          "Orchestrator follows the plan, respects dependencies, and tracks implementation work like a disciplined teammate.",
        component: WhatIsOrchestratorStep,
      },
      {
        id: "run-orchestrator",
        title: "Start execution from your terminal",
        description:
          "Point your CLI host at the epic you want to execute, then let Orchestrator take the first pass at implementation.",
        component: RunOrchestratorStep,
      },
      {
        id: "monitor-progress",
        title: "Follow the work as it happens",
        description:
          "SpecTree keeps the session trail, file context, and validation status visible while Orchestrator is running.",
        component: MonitorProgressStep,
      },
    ],
  },
  {
    id: "review",
    title: "Review & Validate",
    icon: "CheckCircle2",
    description:
      "Close the loop with a thoughtful review process that checks output quality, validation results, and follow-up actions.",
    stepCount: 3,
    estimatedTime: "3 min",
    tone: "rose",
    badge: "Quality gate",
    steps: [
      {
        id: "review-overview",
        title: "Review the finished work",
        description:
          "Use the code context and decision log to understand what changed before you sign off on the result.",
        component: ReviewOverviewStep,
      },
      {
        id: "validate-output",
        title: "Validate beyond the happy path",
        description:
          "Confirm the automated checks passed and add your own human review for edge cases or anything that needs attention.",
        component: ValidateOutputStep,
      },
      {
        id: "iterate",
        title: "Iterate without losing context",
        description:
          "When changes are still needed, SpecTree helps you capture follow-up work and continue the story cleanly.",
        component: IterateStep,
      },
    ],
  },
  /* ------------------------------------------------------------------ */
  /*  Skills                                                             */
  /* ------------------------------------------------------------------ */
  {
    id: "designer",
    title: "SpecTree Designer",
    icon: "Paintbrush",
    description:
      "Build pixel-perfect components from Figma designs, fix existing code to match designs, or run UX audits for accessibility and design-system compliance. Supports Web, iOS, and Android.",
    stepCount: 4,
    estimatedTime: "5 min",
    tone: "blue",
    badge: "Design fidelity",
    steps: [
      {
        id: "what-is-designer",
        title: "What SpecTree Designer does",
        description:
          "Understand how SpecTree Designer supports Build, Fix, and UX Audit workflows across Web, iOS, and Android projects.",
        component: WhatIsDesignerStep,
      },
      {
        id: "designer-platform-select",
        title: "Choose your target platform",
        description:
          "Pick the platform Designer should optimize for so the setup guide shows the right toolchain for your project.",
        component: DesignerPlatformPickerStep,
      },
      {
        id: "designer-prerequisites",
        title: "Set up prerequisites",
        description:
          "Configure the Figma MCP server and install platform-specific tools for screenshots and visual testing.",
        component: DesignerPrerequisitesStep,
      },
      {
        id: "use-designer",
        title: "Run SpecTree Designer on a Figma frame",
        description:
          "Copy a Figma frame link, invoke SpecTree Designer, and watch it build, fix, or audit your implementation with visual proof or an accessibility report.",
        component: UseDesignerStep,
      },
    ],
  },
  {
    id: "pullrequest",
    title: "Automated PR Reviews",
    icon: "GitPullRequest",
    description:
      "Commit, push, create a PR, and run iterative Copilot code reviews — fixing issues and re-reviewing until the PR is clean.",
    stepCount: 3,
    estimatedTime: "3 min",
    tone: "emerald",
    badge: "Ship with confidence",
    steps: [
      {
        id: "what-is-pullrequest",
        title: "What PullRequest does",
        description:
          "Understand the automated PR lifecycle — from commit to clean PR with at least 3 Copilot review cycles.",
        component: WhatIsPullRequestStep,
      },
      {
        id: "run-pullrequest",
        title: "Create and submit a PR",
        description:
          "Invoke the skill from a feature branch and let it handle commits, push, PR creation, and the review loop.",
        component: RunPullRequestStep,
      },
      {
        id: "review-cycle",
        title: "The review cycle",
        description:
          "See how SpecTree PullRequest reads Copilot comments, fixes code, and iterates until both review and CI pass.",
        component: ReviewCycleStep,
      },
    ],
  },
  {
    id: "update",
    title: "Keep SpecTree Updated",
    icon: "Download",
    description:
      "Check for new versions of SpecTree agents, skills, and the MCP server — then apply updates with one command.",
    stepCount: 3,
    estimatedTime: "3 min",
    tone: "amber",
    badge: "Stay current",
    steps: [
      {
        id: "what-is-update",
        title: "Why updates matter",
        description:
          "Skill packs bring new agent behavior and bug fixes. The Update skill checks your installed versions against the registry.",
        component: WhatIsUpdateStep,
      },
      {
        id: "check-updates",
        title: "Check and apply updates",
        description:
          "Invoke the skill to see a version comparison table and apply updates with a single confirmation.",
        component: CheckUpdatesStep,
      },
      {
        id: "update-troubleshooting",
        title: "Troubleshooting",
        description:
          "Handle missing manifests, network errors, and drift detection when things are out of sync.",
        component: UpdateTroubleshootingStep,
      },
    ],
  },
  /* ------------------------------------------------------------------ */
  /*  FogBugz agents                                                     */
  /* ------------------------------------------------------------------ */
  {
    id: "bug-bridge",
    title: "FogBugz Bug-to-ER Bridge",
    icon: "Bug",
    description:
      "Turn a FogBugz case into a SpecTree Epic Request. The Bridge reads the case and hands off to the Request Formulator.",
    stepCount: 3,
    estimatedTime: "4 min",
    tone: "rose",
    badge: "Bug tracking bridge",
    steps: [
      {
        id: "fogbugz-setup-bridge",
        title: "Set up FogBugz",
        description:
          "Make sure your FogBugz API key is configured in Settings before using the Bridge.",
        component: FogBugzSetupStep,
      },
      {
        id: "what-is-bug-bridge",
        title: "What the Bridge does",
        description:
          "Understand how the Bug-to-ER Bridge connects FogBugz cases to SpecTree Epic Requests with full traceability.",
        component: WhatIsBugBridgeStep,
      },
      {
        id: "run-bug-bridge",
        title: "Bridge a FogBugz case",
        description:
          "Give the agent a case ID, answer the Formulator interview for any missing details, and get a Bug-Sourced Epic Request.",
        component: RunBugBridgeStep,
      },
    ],
  },
  {
    id: "fogbugz-formulator",
    title: "File FogBugz Cases",
    icon: "ClipboardList",
    description:
      "Describe a bug in plain English and the FogBugz Formulator turns it into a well-formed case with rubric scoring.",
    stepCount: 3,
    estimatedTime: "4 min",
    tone: "violet",
    badge: "Bug intake",
    steps: [
      {
        id: "fogbugz-setup-formulator",
        title: "Set up FogBugz",
        description:
          "Make sure your FogBugz API key is configured in Settings before filing cases.",
        component: FogBugzSetupStep,
      },
      {
        id: "what-is-fogbugz-formulator",
        title: "How the Formulator works",
        description:
          "See how the intake rubric, targeted clarification, and duplicate detection produce high-quality FogBugz cases.",
        component: WhatIsFogBugzFormulatorStep,
      },
      {
        id: "file-fogbugz-case",
        title: "File a case",
        description:
          "Describe a bug, answer any clarification questions, preview the assembled case, and submit it to FogBugz.",
        component: FileFogBugzCaseStep,
      },
    ],
  },
  {
    id: "fogbugz-inspector",
    title: "FogBugz 360 Inspector",
    icon: "Telescope",
    description:
      "Get a unified status report across FogBugz and SpecTree. Give it a case ID or epic identifier and see both sides.",
    stepCount: 3,
    estimatedTime: "3 min",
    tone: "blue",
    badge: "Cross-system view",
    steps: [
      {
        id: "fogbugz-setup-inspector",
        title: "Set up FogBugz",
        description:
          "Make sure your FogBugz API key is configured in Settings before running inspections.",
        component: FogBugzSetupStep,
      },
      {
        id: "what-is-inspector",
        title: "What the Inspector shows",
        description:
          "Understand the side-by-side report, linkage health checks, and recent activity from both systems.",
        component: WhatIsInspectorStep,
      },
      {
        id: "run-inspector",
        title: "Run an inspection",
        description:
          "Give the agent a FogBugz case ID or SpecTree epic identifier and read the unified 360 report.",
        component: RunInspectorStep,
      },
    ],
  },
];
